import React, {
  ReactElement,
  FunctionComponent,
  useState,
  useEffect,
} from "react";
import { useWindowSize } from "react-use";
import { useAppSelector, useAppDispatch } from "../../../state/hooks";
import Position from "../../../enums/Position";

import WDBuildCounts from "../WDBuildCounts";
import Season from "../../../enums/Season";
import WDPositionContainer from "../WDPositionContainer";
import PhaseSelectorSimple from "../phase-selector/PhaseSelectorSimple";
import { ReactComponent as BtnArrowIcon } from "../../../assets/svg/btnArrow.svg";

import {
  gameViewedPhase,
  gameApiSliceActions,
  gameOverview,
  gameStatus,
} from "../../../state/game/game-api-slice";
import { getHistoricalPhaseSeasonYear } from "../../../utils/state/getPhaseSeasonYear";
import { formatPhaseForDisplay } from "../../../utils/formatPhaseForDisplay";
import { abbrMap } from "../../../enums/Country";
import t from "../../../i18n";

interface BottomMiddleProps {
  viewedSeason: Season;
  viewedYear: number;
  viewedPhase: string;
  totalPhases: number;
}

/*
 * 「进入下一阶段」提示。
 *
 * 关键：标签取「当前正在查看阶段的**下一个**阶段」（viewedPhaseIdx + 1），
 * 而不是最新阶段（overview）。原来用最新阶段时，游戏一次推进多个阶段
 * （例如 春→夏撤退自动结算→秋），按钮会直接显示「秋」、把中间的「夏」跳过，
 * 点一下也直接跳到最新。改成只前进一个阶段后，玩家就能按 春 → 夏 → 秋 的顺序
 * 走完；中间「无需操作」的阶段由服务端自动结算掉。
 */
const NextPhase = function (): ReactElement {
  const dispatch = useAppDispatch();
  const status = useAppSelector(gameStatus);
  const { viewedPhaseIdx } = useAppSelector(gameViewedPhase);

  // 下一个阶段（getHistoricalPhaseSeasonYear 对越界有兜底）
  const {
    phase: nextPhase,
    season: nextSeason,
    year: nextYear,
  } = getHistoricalPhaseSeasonYear(status, viewedPhaseIdx + 1);

  return (
    <div className="flex display-block px-5 sm:px-10 py-5 mt-1 bg-[#1C2B33] rounded-xl text-white items-center select-none w-fit mb-3 mx-auto">
      <div>
        <div className="text-xs">{t("Next phase")}</div>
        <div className="text-sm font-bold uppercase">
          {t(nextSeason)} {nextYear} {formatPhaseForDisplay(nextPhase)}
        </div>
      </div>
      <div className="ml-4">
        {/* 只前进一个阶段；不要在图标上再挂一个 handler（会与按钮重复触发） */}
        <button
          type="button"
          onClick={() => {
            dispatch(gameApiSliceActions.changeViewedPhaseIdxBy(1));
          }}
        >
          <BtnArrowIcon className="text-white stroke-black cursor-pointer rotate-90" />
        </button>
      </div>
      {/*
       * 落后不止一个阶段时，给一个「直接回到当前阶段」的入口。
       * 原因：保存/就绪按钮只有在「正在查看最新阶段」时才会启用，落后时是灰的
       * （自动保存也不会发请求）—— 玩家会以为"指令存不上"。人机局阶段推进快，
       * 很容易落后好几步，没有这个入口就只能一下下点。
       */}
      {viewedPhaseIdx < status.phases.length - 2 && (
        <button
          type="button"
          className="ml-4 text-xs underline opacity-80 hover:opacity-100"
          onClick={() => {
            dispatch(gameApiSliceActions.setViewedPhaseToLatest());
          }}
        >
          {t("Jump to current phase")}
        </button>
      )}
    </div>
  );
};

const BottomMiddle: FunctionComponent<BottomMiddleProps> = function ({
  viewedSeason,
  viewedYear,
  viewedPhase,
  totalPhases,
}: BottomMiddleProps): ReactElement {
  const { viewedPhaseIdx } = useAppSelector(gameViewedPhase);
  const { width } = useWindowSize();
  const [isNewPhase, setIsNewPhase] = useState<boolean>(false);
  const [lastPhase, setLastPhase] = useState<number>(-1);
  const { members } = useAppSelector(gameOverview);

  useEffect(() => {
    // 服务端推进出新阶段、且玩家还没看到最新阶段时，显示「进入下一阶段」提示。
    if (
      lastPhase !== totalPhases &&
      lastPhase !== -1 &&
      viewedPhaseIdx < totalPhases - 1
    ) {
      setIsNewPhase(true);
    }
    // 只有在追上最新阶段后才收起提示：这样从「春」出发会先出现「去夏」，
    // 点进去后自动结算的中转阶段（如无动作的撤退）不打断流程，接着出现「去秋」，
    // 逐个阶段前进，直到停在最新阶段。适用于所有回合。
    if (viewedPhaseIdx >= totalPhases - 1) {
      setIsNewPhase(false);
    }
    setLastPhase(totalPhases);
  }, [viewedPhaseIdx, totalPhases]);

  const centerCounts = [...members]
    .sort((a, b) => a.countryID - b.countryID)
    .map((m) => (
      <span key={m.countryID} className="p-1">
        {abbrMap[m.country] ?? m.country.substring(0, 3)}: {m.supplyCenterNo}
      </span>
    ));

  return (
    <WDPositionContainer
      position={Position.BOTTOM_MIDDLE}
      bottom={width < 500 ? 14 : 4}
    >
      <WDBuildCounts />
      {width > 650 && (
        <div className="bg-[#1C2B33] text-white items-center p-1 m-2 font-medium uppercase text-xs">
          {centerCounts}
        </div>
      )}

      {isNewPhase ? (
        <NextPhase />
      ) : (
        <PhaseSelectorSimple
          viewedSeason={viewedSeason}
          viewedYear={viewedYear}
          viewedPhase={viewedPhase}
          totalPhases={totalPhases}
        />
      )}
    </WDPositionContainer>
  );
};

export default BottomMiddle;
